import {combineLatest, Observable, ReplaySubject, Subject} from 'rxjs';
import {debounceTime, map, take, takeUntil, tap} from 'rxjs/operators';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {FormsModule, NgForm} from '@angular/forms';
import {MatSlideToggleChange, MatSlideToggleModule} from '@angular/material/slide-toggle';
import {DataService} from '../../services/data.service';


import {AuthService} from '../../services/auth.service';
import {AppStateService} from '../../services/app-state.service';

import {Budget, GeneratedBy, Material, SubBudget, SubBudgetTotals} from '../../interfaces';
import {ActivitiesValueModel} from '../models/activities-value';
import {BudgetModel} from '../models/budget-model';
import {SubBudgetDataSource} from './SubBudgetDataSource';
import {MaterialDialogComponent} from './material-dialog.component';
import {CommonModule} from '@angular/common';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatChipInputEvent, MatChipsModule} from '@angular/material/chips';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatTableModule} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {A11yModule} from '@angular/cdk/a11y';



@Component({
    selector: 'app-sub-budget',
    styleUrls: ['./sub-budget.component.css'],
    templateUrl: './sub-budget.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatToolbarModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        MatChipsModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatSlideToggleModule,
        MatExpansionModule,
        MatTableModule,
        MatTooltipModule,
        A11yModule
    ]
})
export class SubBudgetComponent implements OnInit, OnDestroy {

    private _budget: Budget;
    @Input()
    public set budget(b: Budget) {

        this.budget$.next(b);
        this._budget = b;
    }

    public get budget() {
        return this._budget;
    }

    @Input() public subBudgetId: string;
    @Input() public budgetTotal: number;

    @Output() public update = new EventEmitter<SubBudgetTotals>();
    @Output() public delete = new EventEmitter<string>();
    @Output() public tagsUpdated = new EventEmitter<string[]>();
    @Output() public updateCostSummary = new EventEmitter<string>();

    private _canEdit = false;
    get canEdit(): boolean {
        return this._canEdit;
    }

    // Set our default values
    public separatorKeyCodes = [ENTER, COMMA];
    public subBudgetTags: string[] = [];
    public activitiesDataSource: SubBudgetDataSource;
    public materialDataSource: SubBudgetDataSource;
    public activitiesColumns = ['name', 'category', 'cost', 'hoursNew', 'importoCalc', 'weight'];
    public materialsColumns = ['description', 'category', 'unit', 'quantity', 'costEach', 'amount', 'weight', 'actions'];
    public avm: {};
    public subBudgetTotals = new SubBudgetTotals();
    public subBudgetName = '';
    public isEditingName = false;
    public vacancyBtn = false;
    public vacancyBtnDisabled = false;
    public calculate: boolean;
    public calculateToggleEnabled = true;
    public categoryMap = new Map<string, string>();
    public canNotDelete = false;

    private readonly budget$ = new ReplaySubject<Budget>(1);
    private subBudget$: Observable<SubBudget>;
    private subBudgetActivities$: any;
    private subBudgetMaterials$: any;
    private activityValMap: ActivitiesValueModel;
    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        public dialog: MatDialog,
        private auth: AuthService,
        private appState: AppStateService
    ) {
    }

    public ngOnInit() {

        // FIX import assumption
        if (!this.subBudgetId) {
            this.subBudgetId = 'budget0';
        }

        this.initStreams();
        this.initSubscriptions();

        this.activitiesDataSource = new SubBudgetDataSource(this.subBudgetActivities$);
        this.materialDataSource = new SubBudgetDataSource(this.subBudgetMaterials$);
    }

    public ngOnDestroy() {
        this.budget$.complete();
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    private initStreams(): void {
        this.initCategories();
        this.initSubBudgetStream();
        this.initActivitiesStream();
        this.initMaterialsStream();
    }

    private initCategories() {
        this.dataService.getItemCategories().pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(cats => {
            this.categoryMap.clear();
            cats.forEach(c => this.categoryMap.set(c.name, c.description));
        });
    }

    private initSubscriptions(): void {

        this.dataService.getSBActivity(this.budget.id, this.subBudgetId, 'VACANCY').pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(snap => {
            if (snap) {
                this.vacancyBtn = false;
            }
        });

        combineLatest([this.budget$, this.subBudget$]).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(([budget, subBudget]) => {
            this.setPermissionHandler(budget, subBudget);
        });
    }

    private initSubBudgetStream(): void {
        this.subBudget$ = this.dataService.getSubBudget(this.budget.id, this.subBudgetId).pipe(
            tap((sb: SubBudget) => {
                if (!sb) {
                    return;
                }

                this.subBudgetTags = sb.tags;
                this.subBudgetName = sb.description;
                this.calculateToggleEnabled = (!sb.generatedBy || sb.generatedBy === GeneratedBy.User);

                this.handleLegacySubBudgets(sb);

                if (this.calculate !== sb.calculate) {
                    this.calculate = sb.calculate;
                    this.subBudgetTotals.calculate = this.calculate;
                    this.update.emit(this.subBudgetTotals);
                }
            })
        );
    }

    private initActivitiesStream(): void {
        this.subBudgetActivities$ = this.dataService.getSBActivities(this.budget.id, this.subBudgetId, this.categoryMap).pipe(
            tap(activities => {
                if (activities.some(a => a.finalHours && a.finalHours > 0)) {
                    this.canNotDelete = true;
                }

                this.activityValMap = new ActivitiesValueModel(activities);
                this.avm = this.activityValMap.activityValueMap;
                this.subBudgetTotals.activities = activities
                    .filter(a => a.cost && a.hours)
                    .reduce((sum, a) => sum + (a.cost * a.hours), 0);
                this.subBudgetTotals.calculate = this.calculate;
                this.update.emit(this.subBudgetTotals);
            })
        );
    }

    private initMaterialsStream(): void {
        this.subBudgetMaterials$ = combineLatest([
            this.dataService.getSubBudgetResume(this.budget.id),
            this.dataService.getSBMaterials(this.budget.id, this.subBudgetId)
        ]).pipe(
            map(([resume, materials]) => {
                const resumeMaterials = resume?.materials || [];
                return materials.map((data: any) => {
                    const resumeMaterial = resumeMaterials.find(rm => {
                        const sameDescr = rm.description ? rm.description.trim().toLowerCase() === data?.description?.trim().toLowerCase() : true;
                        const sameCategory = rm.category ? rm.category.trim().toLowerCase() === data?.category?.trim().toLowerCase() : true;
                        const sameCostEach = rm.costEach ? rm.costEach === data?.costEach : true;
                        return sameDescr && sameCategory && sameCostEach;
                    });

                    (data as any).hasOrders = !!(resumeMaterial && resumeMaterial.finalQuantity > 0);
                    return data;
                });
            }),
            tap(materials => {
                if (materials.some(m => (m as any).hasOrders)) {
                    this.canNotDelete = true;
                }
                this.subBudgetTotals.materials = materials
                    .filter(m => m.costEach && m.quantity)
                    .reduce((sum, m) => sum + (m.costEach * m.quantity), 0);
                this.subBudgetTotals.calculate = this.calculate;
                this.update.emit(this.subBudgetTotals);
            })
        );
    }

    private setPermissionHandler(budget: Budget, subBudget: SubBudget): void {
        this.auth.userHasPermissions([
            'app.budget.write',
            'app.iproject.write',
            'app.budget.edit.others'
        ]).pipe(takeUntil(this.unsubscribe$))
            .subscribe(([canEditBudget, canEditIproject, canEditOtherBudget]) => {
                this.vacancyBtn = budget.project?.internal && canEditIproject;
                this._canEdit = this.userCanEditSubBudget(
                    budget, subBudget, (canEditBudget || canEditIproject), canEditOtherBudget
                );

                const baseActivityCols = ['name', 'category', 'cost'];
                const baseMaterialCols = ['description', 'category', 'unit', 'quantity', 'costEach', 'amount', 'weight'];

                this.activitiesColumns = [...baseActivityCols, this._canEdit ? 'hoursNew' : 'hours', 'importoCalc', 'weight'];
                this.materialsColumns = this._canEdit ? [...baseMaterialCols, 'actions'] : baseMaterialCols;
            });
    }

    public addTag(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value) {
            this.subBudgetTags = this.subBudgetTags ? [...this.subBudgetTags, value] : [value];
            this.updateTags();
        }

        if (event.input) {
            event.input.value = '';
        }
    }

    public removeTag(tag: string): void {
        this.subBudgetTags = this.subBudgetTags.filter(t => t !== tag);
        this.updateTags();
    }

    public toggleCalculate($event: MatSlideToggleChange): void {
        console.log('toggle', $event.checked, this.subBudgetId, this.budget.id)
        this.dataService.updateSubBudgetField(this.budget.id, this.subBudgetId, {calculate: !$event.checked})
            .then(() => this.updateCostSummary.emit(this.budget.id))
            .catch(err => console.error('Sub Budget Toggle', err));
    }

    public generateVacancyActivity(): void {
        this.vacancyBtnDisabled = true;
        this.dataService.setSBActivity(this.budget.id, this.subBudgetId, 'VACANCY', {
            activity: 'Assenza',
            category: 'ASS',
            cost: 46,
            name: 'Assenza'
        }).finally(() => this.vacancyBtnDisabled = false);
    }

    public deleteSubBudget(): void {
        if (!this.canEdit) {
            return;
        }
        this.appState.confirmDialog({confirmMessage: `SubBudget "${this.subBudgetName}" will be deleted`})
            .then(result => {
                if (result?.confirm) {
                    return this.dataService.deleteSubBudget(this.budget.id, this.subBudgetId).then(() => {
                        this.delete.emit(this.subBudgetId);
                        this.updateCostSummary.emit(this.budget.id);
                    });
                }
                return Promise.resolve();
            });
    }

    public async copySubBudget(): Promise<void> {
        await this.dataService.copySubBudget(this.budget.id, this.subBudgetId, GeneratedBy.User);
    }

    public updateName(name: string): void {
        if (name.length > 2 && (name !== this.subBudgetName)) {
            this.dataService.patchSubBudget(this.budget.id, this.subBudgetId, {description: name});
        }
    }

    public openMaterialDialog(type: 'add' | 'edit' | 'copy', m?: Material): void {
        this.dialog.open(MaterialDialogComponent, {
            width: '520px',
            data: {
                budgetId: this.budget.id,
                subBudgetId: this.subBudgetId,
                type,
                material: m
            }
        });
    }

    public deleteMaterial(row: Material): void {
        this.dataService.deleteMaterial(this.budget.id, this.subBudgetId, row.id).pipe(
            take(1),
            debounceTime(2000)
        ).subscribe(() => {
            this.updateCostSummary.emit(this.budget.id);
            console.log('Deleted material');
        });
    }

    public commitActivities(f: NgForm): void {
        if (f.value) {
            this.dataService.commitActivities(this.budget.id, this.subBudgetId, this.avm).pipe(
                take(1)
            ).subscribe(() => console.log('Activities committed'));
        }
    }

    private updateTags(): void {
        this.dataService.updateSubBudgetField(this.budget.id, this.subBudgetId, {tags: this.subBudgetTags})
            .then(() => this.tagsUpdated.emit(this.subBudgetTags))
            .catch(err => console.error('Sub Budget Tags error ', err));
    }

    private userCanEditSubBudget(budget: Budget,
                                 subBudget: SubBudget,
                                 canEdit: boolean,
                                 canEditOtherBudget: boolean): boolean {
        if (subBudget && subBudget.description === 'Installation') {
            return false;
        }

        return BudgetModel.genericEditBudgetPermission(
            budget,
            this.auth.currentUser,
            canEdit,
            canEditOtherBudget
        );
    }

    /**
     * This is a workaround to fix old sub-budgets that don't have the `generatedBy` field.
     * This should be removed after the data is migrated.
     */
    private handleLegacySubBudgets(sb: SubBudget): void {
        if (sb.generatedBy) {
            return;
        }

        let generatedBy: GeneratedBy;
        if (['ASSEMBLY', 'AUTO_BUDGET', 'IMPORT'].includes(sb.id)) {
            generatedBy = sb.id === 'ASSEMBLY' ? GeneratedBy.Assembly : GeneratedBy.Export;
        } else {
            generatedBy = GeneratedBy.User;
        }

        this.dataService.updateSubBudgetField(this.budget.id, this.subBudgetId, {
            generatedBy,
            calculate: true
        }).then(() => {
            console.log(`FIX HAS BEEN APPLIED Once for sub-budget ${this.subBudgetId}`);
        }).catch(err => {
            console.error(err);
        });
    }
}